import { Component, DestroyRef, effect, inject, input, model, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { fadeIn } from '../../animations/fadeIn';
import { LoaderService } from './loader.service';

@Component({
  selector: 'lib-loader',
  templateUrl: './loader.component.html',
  styleUrls: ['./loader.component.scss'],
  animations: [fadeIn],
  standalone: false,
})
export class LoaderComponent implements OnDestroy {
  private readonly destroyRef$ = inject(DestroyRef);
  private readonly loader = inject(LoaderService);

  /** Show load spinner when `isLoading` is true. */
  isLoading = model(false);
  /** The size of the spinner */
  diameter = input(30);
  /** The bold-ness of the spinner */
  strokeWidth = input(4);
  /** If false, it will try to overlay on the parent element */
  inline = input(false);

  /**
   * If global loader is enabled, the `isLoading` state will be controlled by the
   * loader.interceptor and will show a load spinner on http requests.
   */
  useGlobalLoader = input(false);
  private globalLoader$ = this.loader.isLoading$().pipe(takeUntilDestroyed(this.destroyRef$), distinctUntilChanged());
  private globalLoader = toSignal(this.globalLoader$);
  private _timeout: ReturnType<typeof setTimeout> | undefined = undefined;

  private globalLoaderEffect = effect(() => {
    if (this.useGlobalLoader()) {
      const on = this.globalLoader();

      if (on === true) {
        this.isLoading.set(true);
      } else {
        // Add a small delay before hiding the spinner for visual smoothness
        if (this._timeout) {
          clearTimeout(this._timeout);
        }
        this._timeout = setTimeout(() => {
          this.isLoading.set(false);
        }, 100);
      }
    }
  });

  get size() {
    return `${this.diameter()}px`;
  }

  ngOnDestroy(): void {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }
    this.isLoading.set(false);
  }
}
